/**
 * El tema con el que nace un sitio.
 *
 * Va con los tipos y con la derivación, no en la aplicación del editor: es el
 * punto de partida del que el panel siembra la marca cuando un proyecto todavía
 * no tiene una, y leerlo desde el editor obligaría al panel a depender de él.
 */
import type { StyleProperties } from '@webbuilder/style-engine'
import type { PageTheme } from './types'

export const DEFAULT_PAGE_THEME: PageTheme = {
  presetId: 'saas-2027',
  typography: {
    fontPairId: 'saas-2027',
    headingFont: "'Inter', sans-serif",
    bodyFont: "'Inter', sans-serif",
  },
  colors: {
    background: '#FAFBF7',
    backgroundSecondary: '#F1F3E9',
    card: '#FAFBF7',
    surface: '#E1E8D0',
    border: '#B2B9A5',
    text: '#0B0D0A',
    muted: '#58692C',
    primary: '#0B0D0A',
    accent: '#D7FF64',
  },
  roles: {
    section: {
      background: 'var(--wb-background)',
      color: 'var(--wb-text)',
    },
    heading1: {
      color: 'var(--wb-text)',
      fontFamily: "'Inter', sans-serif",
      fontSize: '64px',
      fontWeight: '300',
      lineHeight: '1.02',
      letterSpacing: '-0.5px',
    },
    heading2: {
      color: 'var(--wb-text)',
      fontFamily: "'Inter', sans-serif",
      fontSize: '2.8rem',
      fontWeight: '300',
      lineHeight: '1.08',
      letterSpacing: '-0.5px',
    },
    heading3: {
      color: 'var(--wb-text)',
      fontFamily: "'Inter', sans-serif",
      fontSize: '1.55rem',
      fontWeight: '300',
      lineHeight: '1.2',
      letterSpacing: '0',
    },
    heading4: {
      color: 'var(--wb-text)',
      fontFamily: "'Inter', sans-serif",
      fontSize: '1.15rem',
      fontWeight: '500',
      lineHeight: '1.3',
      letterSpacing: '0',
    },
    heading5: {
      color: 'var(--wb-text)',
      fontFamily: "'Inter', sans-serif",
      fontSize: '0.95rem',
      fontWeight: '500',
      lineHeight: '1.35',
      letterSpacing: '-0.5px',
    },
    heading6: {
      color: 'var(--wb-text)',
      fontFamily: "'Inter', sans-serif",
      fontSize: '0.78rem',
      fontWeight: '500',
      lineHeight: '1.35',
      letterSpacing: '0.04em',
    },
    paragraph: {
      color: 'var(--wb-text)',
      fontFamily: "'Inter', sans-serif",
      fontSize: '16px',
      fontWeight: '400',
      lineHeight: '1.5',
      letterSpacing: '0',
    },
    smallText: {
      color: 'var(--wb-text)',
      opacity: '0.6',
      fontFamily: "'Inter', sans-serif",
      fontSize: '13px',
      fontWeight: '400',
      lineHeight: '1.5',
      letterSpacing: '0',
      textTransform: 'none',
    },
    eyebrow: {
      color: 'var(--wb-text)',
      opacity: '0.6',
      fontFamily: "'Inter', sans-serif",
      fontSize: '11px',
      fontWeight: '400',
      lineHeight: '1.25',
      letterSpacing: '0.12em',
      textTransform: 'uppercase',
      display: 'block',
      padding: '0',
      borderWidth: '0',
      borderStyle: 'none',
      borderColor: 'transparent',
      background: 'transparent',
    } as StyleProperties,
    buttonPrimary: {
      background: 'var(--wb-muted)',
      color: 'var(--wb-background)',
      fontFamily: "'Inter', sans-serif",
      fontSize: '14px',
      fontWeight: '400',
      letterSpacing: '0',
      borderRadius: '24px',
      padding: '12px 24px',
      width: 'fit-content',
      minHeight: '46px',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderWidth: '0px',
      borderStyle: 'solid',
      borderColor: 'var(--wb-muted)',
      cursor: 'pointer',
      boxShadow: '0 8px 24px rgba(15,23,42,0.00)',
      textDecoration: 'none',
    },
    buttonSecondary: {
      background: 'transparent',
      color: 'var(--wb-muted)',
      fontFamily: "'Inter', sans-serif",
      fontSize: '14px',
      fontWeight: '400',
      letterSpacing: '0',
      borderRadius: '24px',
      padding: '12px 22px',
      width: 'fit-content',
      minHeight: '46px',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderWidth: '1.5px',
      borderStyle: 'solid',
      borderColor: 'var(--wb-muted)',
      cursor: 'pointer',
      textDecoration: 'none',
    },
    card: {
      background: 'var(--wb-card)',
      backgroundColor: 'var(--wb-card)',
      color: 'var(--wb-text)',
      borderRadius: '24px',
      borderColor: 'var(--wb-border)',
      borderWidth: '1px',
      borderStyle: 'solid',
      boxShadow: 'none',
      padding: '42px',
      paddingTop: '42px',
      paddingRight: '42px',
      paddingBottom: '42px',
      paddingLeft: '42px',
    },
    image: {
      borderRadius: '8px',
      objectFit: 'cover',
      objectPosition: 'center',
      overflow: 'hidden',
      boxShadow: 'none',
    },
    /* Un icono de fábrica: 20 px de símbolo y 8 de aire —una caja de 36—. Eran
       24 y 10 (44 px), y en una sección con varios iconos, o al seleccionar uno,
       pesaban más que el texto que acompañan. */
    icon: {
      color: 'var(--wb-primary)',
      background: 'var(--wb-backgroundSecondary)',
      borderRadius: '24px',
      padding: '8px',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    badge: {
      color: 'var(--wb-text)',
      fontFamily: "'Inter', sans-serif",
      fontSize: '11px',
      fontWeight: '650',
      lineHeight: '1.2',
      letterSpacing: '0.02em',
      textTransform: 'none',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: 'fit-content',
      padding: '3px 8px',
      borderRadius: '8px',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: 'rgba(15,23,42,0.14)',
      background: 'var(--wb-surface)',
    } as StyleProperties,
    rating: {
      color: 'var(--wb-accent)',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '3px',
    },
    avatarGroup: {
      display: 'flex',
      alignItems: 'center',
      gap: '0px',
    },
    stat: {
      background: 'var(--wb-card)',
      color: 'var(--wb-text)',
      borderRadius: '8px',
      borderColor: 'var(--wb-border)',
      borderWidth: '1px',
      borderStyle: 'solid',
      boxShadow: '0 4px 16px rgba(15,23,42,0.06)',
      padding: '20px',
      fontFamily: "'Inter', sans-serif",
      fontSize: '1.55rem',
      fontWeight: '850',
      lineHeight: '1.2',
    },
    link: {
      background: 'transparent',
      color: 'var(--wb-primary)',
      fontFamily: "'Inter', sans-serif",
      fontSize: '14px',
      fontWeight: '400',
      letterSpacing: '0',
      borderRadius: '24px',
      padding: '10px 0',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      textDecoration: 'none',
    },
    navigation: {
      background: 'var(--wb-accent)',
      color: 'var(--wb-text)',
      borderColor: 'var(--wb-border)',
      borderWidth: '1px',
      borderStyle: 'solid',
      paddingTop: '16px',
      paddingBottom: '16px',
      paddingLeft: '24px',
      paddingRight: '24px',
      boxShadow: '0 4px 16px rgba(15,23,42,0.04)',
    },
    footer: {
      background: 'var(--wb-text)',
      color: 'var(--wb-background)',
      paddingTop: '64px',
      paddingBottom: '64px',
      paddingLeft: '24px',
      paddingRight: '24px',
    },
  },
  roleProps: {
    icon: { iconSize: 20 },
    badge: { iconSize: 14 },
    rating: { ratingValue: 5, ratingSize: 16 },
    avatarGroup: { avatarCount: 4, avatarSize: 36 },
  },
  // Responsive type scale. Desktop sizes live in `roles`; these only shrink the
  // larger headings on smaller screens (best-practice fluid scale). Body/párrafo and
  // small stay put — body never drops below 16px to avoid iOS auto-zoom.
  roleBreakpoints: {
    tablet: {
      heading1: { fontSize: '44px' },
      heading2: { fontSize: '36px' },
      heading3: { fontSize: '22px' },
    },
    mobile: {
      heading1: { fontSize: '34px', lineHeight: '1.08' },
      heading2: { fontSize: '28px', lineHeight: '1.12' },
      heading3: { fontSize: '20px' },
      heading4: { fontSize: '17px' },
    },
  },
}
